import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Chip, MenuItem, Select, Stack, Typography, Menu, IconButton } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DistributionCenterService from '@api/DistributionCenters/DistributionCenterCtrl';
import InventoryOrderService from '@api/InventoryOrders/InventoryOrderCtrl';
import { hasPermission } from '@utils/Permissions';
import { ProfileTypes } from '@api/Users/User';
import {
    InventoryOrder,
    InventoryOrderStatus,
    getInventoryOrderStatusName,
    getInventoryOrderStatusColor,
    getInventoryOrderPaymentStatusName,
    getInventoryOrderPaymentStatusColor
} from '@api/InventoryOrders/InventoryOrder';
import ShoppingListPDF from '@screens/Cedis/components/ShoppingListPDF';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { setActivityDrawer } from '@store/appStore';
import { buildInventoryOrderOrigin } from '@api/ActivityLogs/InventoryOrderActivity';

const ListInventoryOrdersScreen: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const navigation = useNavigate();

    // Redux State
    const { loggedUser, distributionCenterSelected } = useSelector((state: RootReducer) => state.auth);
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);

    // Servicios
    const distributionCenterService = useMemo(() => new DistributionCenterService(), []);
    const inventoryOrderService = useMemo(() => new InventoryOrderService(), []);

    // Estados Locales
    const [currentDistributionCenterId, setCurrentDistributionCenterId] = useState<string | undefined>(undefined);
    const [currentStatus, setCurrentStatus] = useState<string>('all');
    const [inventoryOrders, setInventoryOrders] = useState<InventoryOrder[]>([]);
    const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);

    // Estado del menú de acciones masivas
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openStatusMenu = Boolean(anchorEl);

    // Estado del menú de acciones de fila
    const [rowAnchorEl, setRowAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOrder, setSelectedOrder] = useState<InventoryOrder | null>(null);

    const handleRowMenuOpen = (event: React.MouseEvent<HTMLElement>, row: InventoryOrder) => {
        setRowAnchorEl(event.currentTarget);
        setSelectedOrder(row);
    };

    const handleRowMenuClose = () => {
        setRowAnchorEl(null);
        setSelectedOrder(null);
    };

    const handleViewOrder = () => {
        if (selectedOrder) {
            onShowInventoryOrder(selectedOrder);
        }
        handleRowMenuClose();
    };

    /**
     * La bitácora del pedido de insumos (M6). Es donde el CEDIS marca "Recibido",
     * que mueve el inventario de dos lugares a la vez y se puede hacer en masa.
     *
     * El primer renglón se DERIVA (D8): `createdAt` está en 959/959 pedidos y
     * `createdBy` en el 27 % — los recientes sí, los de 2023 no. Es justo el caso
     * que tiene que verse bien SIN autor. Cero lecturas: sale de la fila.
     */
    const handleOrderActivity = () => {
        const order = selectedOrder;
        handleRowMenuClose();
        if (!order?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'inventoryOrder',
                sectionId: order.id,
                derived: buildInventoryOrderOrigin(order)
            })
        );
    };

    // 1. Obtener y setear el ID del Centro de Distribución
    useEffect(() => {
        const fetchDistributionCenterId = async () => {
            showLoader();
            try {
                let id = distributionCenterSelected?.id;

                if (
                    !id &&
                    hasPermission([ProfileTypes.DistributionCenter]) &&
                    loggedUser?.profile.profile !== ProfileTypes.Admin &&
                    loggedUser?.uid
                ) {
                    const distributionCenterData = await distributionCenterService.getDistributionCenterByManagerId(
                        loggedUser.uid
                    );
                    id = distributionCenterData?.id;
                }

                setCurrentDistributionCenterId(id);
            } catch (error: any) {
                showAlertError(error);
            }
            hideLoader();
        };

        void fetchDistributionCenterId();
    }, [distributionCenterSelected, loggedUser, distributionCenterService]);

    // 2. Cargar las órdenes de inventario cuando cambia el CD o el estatus
    const fetchOrders = async () => {
        if (!currentDistributionCenterId) return;

        showLoader();
        try {
            // Si currentStatus es 'all', pasamos undefined al API
            const statusFilter = currentStatus === 'all' ? undefined : currentStatus;
            const dataInventory = await inventoryOrderService.getInventoryOrders(
                undefined,
                currentDistributionCenterId,
                statusFilter
            );
            setInventoryOrders(dataInventory);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void fetchOrders();
    }, [currentDistributionCenterId, currentStatus]);

    // Generar PDF
    const onShoppingList = async () => {
        showLoader();
        try {
            if (currentDistributionCenterId) {
                const distributionCenter = await distributionCenterService.getDistributionCenter(
                    currentDistributionCenterId
                );
                const items = await inventoryOrderService.getShoppingList(currentDistributionCenterId);

                if (items.length === 0) {
                    alert('No hay items para generar la lista de compras');
                    hideLoader();
                    return;
                }

                const blob = await pdf(
                    <ShoppingListPDF items={items} distributionCenter={distributionCenter} />
                ).toBlob();

                const fileName = `Lista_de_Compras_${distributionCenter?.name || ''}_${format(
                    new Date(),
                    'yyyyMMdd'
                )}.pdf`;
                saveAs(blob, fileName);
            }
        } catch (error: any) {
            console.error('Error al generar el PDF:', error);
            showAlertError(error);
        }
        hideLoader();
    };

    // Acciones de la tabla
    const onShowInventoryOrder = (inventoryOrder: InventoryOrder) => {
        navigation('details', { state: { inventoryOrderId: inventoryOrder.id } });
    };

    // Funciones de utilidad para las columnas
    const getTotal = (inventoryOrder: InventoryOrder) => {
        return inventoryOrder.items
            .reduce((total, item) => total + (item.quantity || 0) * item.input.costOfSale, 0)
            .toFixed(2);
    };

    const getBranchOfficeName = (branchOfficeId: string) => {
        const branchOfficeData = branchOffices.find((b) => b.id === branchOfficeId);
        return branchOfficeData?.name || '-';
    };

    const getStatusIcon = (status: InventoryOrderStatus) => {
        switch (status) {
            case InventoryOrderStatus.requested:
                return <HelpOutlineIcon fontSize="small" />;
            case InventoryOrderStatus.pickupReady:
                return <InventoryIcon fontSize="small" />;
            case InventoryOrderStatus.inRoute:
                return <LocalShippingIcon fontSize="small" />;
            case InventoryOrderStatus.received:
                return <CheckCircleOutlineIcon fontSize="small" />;
            case InventoryOrderStatus.canceled:
                return <CancelIcon fontSize="small" />;
            default:
                return <PendingActionsIcon fontSize="small" />;
        }
    };

    // Manejo de actualización masiva de estatus
    const handleStatusMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleStatusMenuClose = () => {
        setAnchorEl(null);
    };

    const updateStatus = async (newStatus: InventoryOrderStatus) => {
        if (selectedIds.length === 0) return;
        showLoader();
        try {
            const result = await inventoryOrderService.bulkUpdateInventoryOrdersStatus(
                selectedIds as string[],
                newStatus
            );
            await fetchOrders();
            setSelectedIds([]);
            // Un pedido que NO entró al inventario no se marca como recibido: hay que
            // decirlo. Antes el error se quedaba en un console.error y el pedido
            // aparecía "Recibido" sin haber sumado nada.
            if (result.failed.length > 0) {
                showAlertWarning(
                    `${result.updated} de ${selectedIds.length} pedidos actualizados. ${result.failed.length} no se pudieron recibir: vuelve a intentarlo (recibir dos veces no duplica).`
                );
            } else if (result.warnings.length > 0) {
                const unicos = Array.from(new Set(result.warnings));
                showAlertWarning(
                    `${result.updated} pedidos actualizados, con avisos: ${unicos.slice(0, 3).join(' · ')}${
                        unicos.length > 3 ? ` (+${unicos.length - 3} más)` : ''
                    }`
                );
            } else {
                showAlertMessage(`${result.updated} pedidos actualizados`);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        handleStatusMenuClose();
        hideLoader();
    };

    // Definición de columnas
    const columns = useMemo<ResponsiveGridColDef<InventoryOrder>[]>(
        () => [
            {
                field: 'id',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        ID de pedido
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.shortId || row.id}</Typography>
            },
            {
                field: 'Fecha',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Fecha
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{format(row.createdAt.toDate(), 'dd-MM-yyy pp')}</Typography>
            },
            {
                field: 'branchOffice',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Sucursal
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{getBranchOfficeName(row.branchOfficeId)}</Typography>,
                valueGetter: ({ row }) => getBranchOfficeName(row.branchOfficeId)
            },
            {
                field: 'createdBy',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Creado por
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.createdBy?.profile?.name || 'Desconocido'}</Typography>,
                valueGetter: ({ row }) => row.createdBy?.profile?.name || 'Desconocido'
            },
            {
                field: 'items',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Artículos
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.items.length}</Typography>,
                valueGetter: ({ row }) => row.items.length
            },
            {
                field: 'total',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>${getTotal(row)}</Typography>,
                valueGetter: ({ row }) => getTotal(row)
            },
            {
                field: 'paymentStatus',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado del pago
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Chip
                        label={getInventoryOrderPaymentStatusName(row.paymentStatus)}
                        color={getInventoryOrderPaymentStatusColor(row.paymentStatus)}
                        size="small"
                    />
                ),
                valueGetter: ({ row }) => getInventoryOrderPaymentStatusName(row.paymentStatus)
            },
            {
                field: 'status',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Chip
                        label={getInventoryOrderStatusName(row.status)}
                        color={getInventoryOrderStatusColor(row.status)}
                        size="small"
                    />
                ),
                valueGetter: ({ row }) => getInventoryOrderStatusName(row.status)
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleRowMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        [branchOffices]
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} height="100%" sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                {currentDistributionCenterId && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Box>
                            <Button
                                variant="contained"
                                disabled={selectedIds.length === 0}
                                onClick={handleStatusMenuClick}
                                startIcon={<PendingActionsIcon />}
                            >
                                Cambiar estado ({selectedIds.length})
                            </Button>
                            <Menu anchorEl={anchorEl} open={openStatusMenu} onClose={handleStatusMenuClose}>
                                {Object.values(InventoryOrderStatus).map((status) => (
                                    <MenuItem
                                        key={status}
                                        onClick={() => updateStatus(status)}
                                        sx={{ '& .MuiListItemIcon-root': { minWidth: 36 } }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusIcon(status)}
                                            {`Marcar como ${getInventoryOrderStatusName(status)}`}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<AddIcon />}
                                onClick={() => navigation('details', { state: { isNewOrderFromCedis: true } })}
                            >
                                Nuevo Pedido
                            </Button>
                            <Select
                                size="small"
                                value={currentStatus}
                                onChange={(e) => setCurrentStatus(e.target.value)}
                            >
                                <MenuItem value="all">Todos los status</MenuItem>
                                {Object.values(InventoryOrderStatus).map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {getInventoryOrderStatusName(status)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<FileDownloadIcon />}
                                onClick={onShoppingList}
                            >
                                Lista de compras
                            </Button>
                        </Stack>
                    </Stack>
                )}

                {currentDistributionCenterId && (
                    <>
                        <ResponsiveDataGrid
                            rows={inventoryOrders}
                            columns={columns}
                            checkboxSelection
                            onRowSelectionModelChange={(newSelection) => {
                                setSelectedIds(newSelection);
                            }}
                            rowSelectionModel={selectedIds}
                            onCellDoubleClick={(cel) => onShowInventoryOrder(cel.row as InventoryOrder)}
                            disableRowSelectionOnClick
                        />
                        <Menu anchorEl={rowAnchorEl} open={Boolean(rowAnchorEl)} onClose={handleRowMenuClose}>
                            <MenuItem onClick={handleViewOrder}>Ver</MenuItem>
                            <MenuItem onClick={handleOrderActivity}>Actividad</MenuItem>
                        </Menu>
                    </>
                )}
            </Stack>
        </Box>
    );
};

export default ListInventoryOrdersScreen;
